import React from 'react';
import { Submission } from '../../store/submission/types';
import { Link, CancelButton } from '../ui';
import { Answer, Answerable, Form, Question } from '../../store/form/types';
import TabForms from '../TabForms/TabForms';
import { Review } from '../../store/review/types';
import { Role, RoleType } from '../../store/conference/types';
import useTheme from '@material-ui/core/styles/useTheme';
import styled, { ThemeProvider } from 'styled-components';
import EditIcon from '../../icons/Edit';
import history from '../../store/history';
import { fillRoutePath } from '../../helpers/path';
import { getRouteByName } from '../../router/routes';
import { AssignmentPermissionManager } from '../../helpers/permissions';
import StatusLabel from '../StatusLabel/StatusLabel';
import ChipMessage from '../ui/display/Chip/ChipMessage';
import {
  formPendingQuestions,
  isCompletedReadonly,
  isDefaultSubmissionForm,
  isResponsibleForReadonlyForms,
} from '../../helpers/form';
import { useTranslation } from 'react-i18next';
import { getFormName } from '../../helpers/translations';
import DefaultSubmissionForm from '../DefaultSubmissionForm/DefaultSubmissionForm';
import Loading from '../Loading/Loading';
import { getUserAndRoleDisplayName } from '../../helpers/users';
import { AppState } from '../../store/state';
import { selectDefaultQuestionsNumber } from '../../store/selectors';
import { AppDispatch } from '../../store/store';
import { connect } from 'react-redux';
import PrevNextButtonsCtrl from '../PrevNextButtonsCtrl/PrevNextButtonsCtrl';
import PaperStatusLabel from '../PaperStatusLabel/PaperStatusLabel';
import { getPaperStatusCategory, getPaperStatusName } from '../../helpers/submission';
import { useIsMobile } from '../../helpers/window';
import Hash from '../Hash/Hash';

interface Props {
  isLoadingReviews: boolean;
  submission: Submission;
  keywordState: KeywordState;
  roleById: { [key: string]: Role };
  submissionFormsData: {
    answerable: Answerable;
    formsData: { form: Form; questions: Question[]; answers: Answer[] }[];
  }[];
  reviewFormsData: {
    answerable: Answerable;
    formsData: { form: Form; questions: Question[]; answers: Answer[] }[];
  }[];
  canViewAuthors: boolean;
  canViewPaperStatus: boolean;
  currentRoleId: number;
  personId: number;
  onEditClick?: () => void;
  onSendEmailClick?: () => void;
  onViewDiscussionClick?: () => void;
  onPaperStatusEdit?: () => void;
  defaultQuestionsNumber: number;
  onMove: (rowId: number) => void;
  hideOtherPcReviews?: boolean;
}

/**
 * Push a route to conference home or all submissions page depending on role.
 * @param currentRoleType
 */
export const goBackFromSubmissionDetail = (currentRoleType: RoleType): void => {
  const canBack = !!history.location.key; // From https://stackoverflow.com/questions/37385570/how-to-know-if-react-router-can-go-back-to-display-back-button-in-react-app

  if (canBack && history.length > 2) {
    history.goBack();
  } else if (currentRoleType == 'chair') {
    history.push(fillRoutePath(getRouteByName('RouteAllSubmissions').path));
  } else {
    history.push(fillRoutePath(getRouteByName('RouteConferenceHome').path));
  }
};

const SubmissionDetail: React.FC<Props> = ({
  isLoadingReviews,
  submission,
  keywordState,
  roleById,
  submissionFormsData,
  reviewFormsData,
  canViewAuthors,
  canViewPaperStatus,
  currentRoleId,
  personId,
  onEditClick,
  onSendEmailClick,
  onViewDiscussionClick,
  onPaperStatusEdit,
  defaultQuestionsNumber,
  onMove,
  hideOtherPcReviews,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();

  // useState hooks
  // Selected item state used for both, submenu items and external controlled state for tab forms
  const [selectedItemChange, setSelectedItemChange] = React.useState<string | undefined>();

  const currentRole = roleById[currentRoleId];
  const currentRoleType = currentRole.type;

  const displayActionsMenu = onEditClick || onSendEmailClick || onViewDiscussionClick || onPaperStatusEdit;

  // Default form questions content
  const defaultSubmissionFormData = submissionFormsData[0].formsData.find(({ form }) => isDefaultSubmissionForm(form));

  const theme = useTheme();

  // Function to scroll to a title section
  const scrollToTitle = (title: string) => {
    const targetElement = document.querySelector(`[id="${title}"]`);

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /*
    Function to control what submenu or tab element is clicked and scroll to it.
    Submenu items has main: prefix except submission forms list
    To control if clicked in submission form or not through prefix 'main'
    and change behavior
  */
  const onSelectedItemChange = (tabName: string) => {
    const split = tabName.split(':');
    if (split.length > 1 && split[0] === 'main') {
      scrollToTitle(split[1]);
    } else {
      scrollToTitle('paper-information');
      setSelectedItemChange(tabName);
    }
  };

  // JSX elements
  const submissionFormListElements: JSX.Element[] = [];
  const tabFormElements: JSX.Element[] = [];
  const reviewsListElements: JSX.Element[] = [];
  const otherReviewersListElements: JSX.Element[] = [];

  // Submission forms list for "In this page" submenu
  if (submissionFormsData.length > 0) {
    const { answerable, formsData } = submissionFormsData[0];
    formsData.forEach(({ form, questions, answers }) => {
      const numPending = formPendingQuestions(form, questions, answers, answerable);

      submissionFormListElements.push(
        <li key={form.id}>
          <Link
            className={`flex items-center ${selectedItemChange === form.name ? 'font-normal' : ''}`}
            onClick={() => {
              onSelectedItemChange(form.name);
            }}
          >
            <div
              className={`${
                !!onEditClick && numPending > 0 ? 'bg-red-600' : 'bg-black'
              } text-lg ml-1.5 mr-3.5 pt-1 w-1.5 h-1.5 rounded-full`}
            />
            <span className={`${!!onEditClick && numPending > 0 ? 'text-red-600' : ''}`}>
              {getFormName(form.name, form.is_default, t)}
            </span>
          </Link>
        </li>,
      );
    });
  }

  // Review tab forms content and reviews list for "In this page" submenu
  reviewFormsData
    .sort((a, b) => {
      const reviewA = a.answerable as Review;
      const reviewB = b.answerable as Review;
      return reviewA.role_id - reviewB.role_id || reviewA.external_id - reviewB.external_id; // ORDER BY role_id, external_id ASC
    })
    .forEach((data) => {
      const { answerable, formsData } = data;
      const review = answerable as Review;
      const isIncompletedReview = review.validation_status != 'Complete';
      const pm = new AssignmentPermissionManager(review);
      const isOwner = pm.isAuthor(personId, currentRoleId);
      const canEdit = pm.canEdit(
        personId,
        roleById[currentRoleId],
        formsData.map(({ form }) => form),
      );

      const reviewRole = roleById[review.role_id];
      const isHiddenPcReview =
        !!hideOtherPcReviews &&
        !isOwner &&
        (reviewRole.description === 'PC' || reviewRole.description === 'Emergency Review');

      const [rolePart, userPart, hashPart] = getUserAndRoleDisplayName(
        roleById[currentRoleId],
        reviewRole,
        review.person.full_name,
        isOwner,
        t,
        review.external_id,
        review.external_hash,
      );

      const reviewerEl = (
        <>
          <div
            className={`${
              !isHiddenPcReview && canEdit && isIncompletedReview ? 'bg-red-600' : 'bg-black'
            } text-lg ml-1.5 mr-3.5 pt-1 w-1.5 h-1.5 rounded-full`}
          />
          <span
            className={`${
              !isHiddenPcReview && canEdit && isIncompletedReview
                ? 'text-red-600'
                : isHiddenPcReview
                ? 'text-black'
                : ''
            }`}
          >
            {rolePart} {userPart}
          </span>
          <span className="ml-1.5">
            <Hash hash={hashPart} />
          </span>
          {(currentRoleType === 'chair' || isResponsibleForReadonlyForms(formsData, isOwner)) &&
            isCompletedReadonly(formsData) && (
              <span className="text-green-600 ml-1.5" title={t('Completed')}>
                ✓
              </span>
            )}
        </>
      );

      if (!formsData.length) {
        // Other reviewers list for "In this page" submenu
        otherReviewersListElements.push(
          <li key={review.id}>
            <div className="flex items-center text-black">{reviewerEl}</div>
          </li>,
        );
      } else {
        // Review tab forms content
        if (!isHiddenPcReview) {
          tabFormElements.push(
            <div key={review.id} id={`review${review.id}`} className="mb-7">
              <StyledReviewTitle className="flex items-center mobileCtrlReviewerTitle">
                <div className="flex">
                  <div>
                    <span className="font-bold mr-1">{rolePart}</span>
                    <span className="mr-3">{userPart}</span>
                  </div>
                </div>

                {canEdit && isIncompletedReview && (
                  <ChipMessage message={t('Required questions pending')} colorType="warning" />
                )}
              </StyledReviewTitle>
              <TabForms
                data={data}
                mode="view"
                canViewFeedback={canEdit}
                onEditClick={
                  canEdit
                    ? () => {
                        history.push(
                          fillRoutePath(getRouteByName('RouteEditReview').path, {
                            submissionId: submission.external_id.toString(),
                            reviewId: review.external_id.toString(),
                            roleId: review.role_id.toString(),
                          }),
                        );
                      }
                    : undefined
                }
              />
            </div>,
          );
        }

        // Reviews list for "In this page" submenu
        reviewsListElements.push(
          <li key={review.id}>
            {isHiddenPcReview ? (
              <div className="flex items-center text-black">{reviewerEl}</div>
            ) : (
              <Link
                className={`flex items-center ${
                  selectedItemChange?.split(':')[1] === `review${review.id}` ? 'font-normal' : ''
                }`}
                onClick={() => {
                  onSelectedItemChange(`main:review${review.id}`);
                }}
              >
                {reviewerEl}
              </Link>
            )}
          </li>,
        );
      }
    });

  // Render HTML Elements
  const lateralMenu = (
    <StyledSubmenu className="mobileCtrlSubmissionDetailSubmenu">
      {/* PAPER STATUS SUBMENU */}
      {canViewPaperStatus && (
        <div className="mb-10">
          <h2>{t('Paper status')}</h2>
          <div className="flex items-center gap-x-3 mobileCtrlSubmissionDetailEditPaperStatusWrapper">
            <PaperStatusLabel name={getPaperStatusName(submission)} category={getPaperStatusCategory(submission)} />
            {onPaperStatusEdit && (
              <div>
                <Link className="flex items-center cursor-pointer" onClick={onPaperStatusEdit}>
                  <EditIcon color={theme.palette.primary.main} className="mr-1" />
                  {t('Edit')}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ACTIONS SUBMENU */}
      {displayActionsMenu && (
        <div className="mb-10">
          <h2>{t('Actions')}</h2>
          <ul>
            {onEditClick && (
              <li>
                <Link onClick={() => onEditClick()}>{t('Edit submission')}</Link>
              </li>
            )}
            {onSendEmailClick && (
              <li>
                <Link disabled={isMobile} onClick={() => onSendEmailClick()}>
                  {t('Send email')} {isMobile && t('(only desktop)')}
                </Link>
              </li>
            )}
            {onViewDiscussionClick && (
              <li>
                <Link onClick={() => onViewDiscussionClick()}>{t('Join discussion')}</Link>
              </li>
            )}
          </ul>
        </div>
      )}

      {/* IN THIS PAGE SUBMENU */}
      {!isMobile && (
        <StyledInThisPage className="mb-10">
          <h2>{t('In this page')}</h2>

          <ul className="pr-2">
            {/* Submission Forms List in submenu */}
            {submissionFormsData.length > 0 && submissionFormsData[0].formsData.length > 0 && (
              <li>
                <Link
                  onClick={() => {
                    onSelectedItemChange('main:paper-information');
                  }}
                  className={`${
                    (selectedItemChange && !selectedItemChange.includes('main:')) ||
                    selectedItemChange === 'main:paper-information'
                      ? 'font-normal'
                      : ''
                  }`}
                >
                  {t('Submission forms')}
                </Link>
                <ul>{submissionFormListElements}</ul>
              </li>
            )}

            {/* Reviews List in submenu */}
            {isLoadingReviews ? (
              <Loading text="Loading reviews" compact={true} />
            ) : (
              <>
                {reviewsListElements.length > 0 && (
                  <li>
                    <Link
                      onClick={() => {
                        onSelectedItemChange('main:reviews');
                      }}
                      className={`${
                        selectedItemChange && selectedItemChange.includes('main:review') ? 'font-normal' : ''
                      }`}
                    >
                      {t('Reviews')}
                    </Link>
                    <ul>{reviewsListElements}</ul>
                  </li>
                )}
                {otherReviewersListElements.length > 0 && (
                  <li>
                    <span className="text-black">{t('Other reviewers')}</span>
                    <ul>{otherReviewersListElements}</ul>
                  </li>
                )}
              </>
            )}
          </ul>
        </StyledInThisPage>
      )}
    </StyledSubmenu>
  );

  // Render page
  return (
    <ThemeProvider theme={theme}>
      <StyledSubmissionDetailWrapper style={{ flexGrow: 1 }}>
        <div className="flex justify-start h-full mobileCtrlSubmissionDetailContent">
          {/* PAPER MAIN CONTENT */}
          <StyledContentWrapper>
            {/* HEADER */}
            <header className="text-sm font-semibold flex items-center justify-between flex-row mb-8 mobileCtrlSubmissionDetailHeader">
              <PrevNextButtonsCtrl rowId={submission.id} type={'App\\Models\\Submission'} onMove={onMove}>
                <StyledSubmissionId>
                  <p>#{submission.external_id}</p>
                </StyledSubmissionId>
              </PrevNextButtonsCtrl>

              <StyledSubmissionTitleDiv className="mobileCtrlSubmissionDetailTitleWrapper">
                <StyledSubmissionTitle>
                  {submission.title ? submission.title : t('Untitled paper')}
                </StyledSubmissionTitle>

                {onEditClick && submission.validation_status === 'Pending' && (
                  <StyledStatusLabel>
                    <StatusLabel label={t('Submission')} validationStatus={submission.validation_status} />
                  </StyledStatusLabel>
                )}
              </StyledSubmissionTitleDiv>
            </header>

            {isMobile && lateralMenu}

            {/* TABFORMS */}
            {submissionFormsData.length > 0 && (
              <div id="paper-information">
                <TabForms
                  data={submissionFormsData[0]}
                  mode="view"
                  canViewFeedback={!!onEditClick}
                  onFormTabChange={onSelectedItemChange}
                  formTabValue={selectedItemChange}
                  defaultSubmissionQuestions={
                    defaultSubmissionFormData && defaultSubmissionFormData.form.default_questions ? (
                      <DefaultSubmissionForm
                        submission={submission}
                        default_questions={defaultSubmissionFormData.form.default_questions}
                        canViewAuthors={canViewAuthors}
                        keywordState={keywordState}
                        canViewFeedback={!!onEditClick}
                        userRole={currentRole}
                      />
                    ) : undefined
                  }
                  numDefaultQuestions={defaultQuestionsNumber}
                />
              </div>
            )}
            {isLoadingReviews ? (
              <Loading text="Loading reviews" compact={true} />
            ) : (
              tabFormElements.length > 0 && (
                <StyledReviewsSection
                  className="mt-7 submissionDetailReviewsSection mobileCtrlSubmissionReviewsSection"
                  id="reviews"
                >
                  <h2>{t('Reviews')}</h2>

                  {tabFormElements}
                </StyledReviewsSection>
              )
            )}
          </StyledContentWrapper>

          {/* LATERAL SUBMENU */}
          {!isMobile && lateralMenu}
        </div>

        {/* FOOTER BUTTONS */}
        <StyledButtonsWrapper className="mobileCtrlSubmissionDetailFooterButton">
          <CancelButton onClick={() => goBackFromSubmissionDetail(currentRoleType)}>
            {isMobile ? t('Back') : t('Close')}
          </CancelButton>
        </StyledButtonsWrapper>
      </StyledSubmissionDetailWrapper>
    </ThemeProvider>
  );
};

const mapStateToProps = (state: AppState) => ({
  defaultQuestionsNumber: selectDefaultQuestionsNumber(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(SubmissionDetail);

const StyledSubmissionDetailWrapper = styled.div`
  h2 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }
`;

const StyledContentWrapper = styled.div`
  width: 100%;
  max-width: 72rem;
`;

const StyledSubmenu = styled.section`
  position: sticky;
  top: 0;
  align-self: flex-start;
  padding-right: 2rem;
  padding-left: 5rem;

  @media (max-width: 1280px) {
    padding-left: 3rem;
    padding-right: 0;
  }

  ul {
    color: ${(props) => props.theme.palette.text.hint};

    li {
      margin-bottom: 0.5rem;

      a {
        /*color: ${(props) => props.theme.palette.text.hint}!important;*/
        cursor: pointer;
      }
    }
  }

  h2 {
    margin-bottom: 0.5rem;
  }
`;

const StyledInThisPage = styled.div`
  overflow-y: auto;
  max-height: 52vh;

  > ul {
    border-left: 2px solid ${(props) => props.theme.palette.text.hint};
    padding-left: 1rem;

    ul {
      margin-top: 0.5rem;
      padding-left: 1rem;
    }
  }
`;

const StyledSubmissionId = styled.div`
  border-right: 2px solid ${(props) => props.theme.palette.secondary.light};
  background-color: ${(props) => props.theme.palette.secondary.light};
  padding: 0.2rem;
  border-radius: 5px;

  p {
    //font-size: 1rem;
  }
`;

const StyledSubmissionTitleDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  background-color: ${(props) => props.theme.palette.background.paper};
`;

const StyledSubmissionTitle = styled.div`
  flex-grow: 1;
  font-size: 1rem;
  margin-bottom: 0;
  padding-left: 1rem;
`;

const StyledStatusLabel = styled.div`
  > div {
    display: flex;
    align-items: center;
    padding-left: 2rem;

    span {
      padding-right: 0.4rem;
    }

    > div {
      width: auto;
      margin: 0;
      font-weight: normal;
    }
  }
`;

const StyledReviewsSection = styled.div`
  h2 {
    margin-bottom: 1.2rem;
  }
`;

const StyledReviewTitle = styled.div`
  margin-bottom: 0.75rem;
  justify-content: space-between;

  span {
    font-size: 0.875rem;
  }
`;

const StyledButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: sticky;
  width: 100%;
  bottom: 0;
  padding: 1rem 0;
  background-color: #fff;
  border-top: 1px dashed ${(props) => props.theme.palette.secondary.dark};
  z-index: 2;
`;
